<template>
  <div
    ref="modal"
    v-focus
    :aria-label="title"
    :class="isCustomHtml ? $style.modal + ' ' + $style.customHtmlStyles : $style.modal"
    :style="modalStyle"
    :data-qa="idPrefix + 'modal'"
    role="dialog"
    @mousedown="closeByBackgroundClick"
  >
    <div ref="clickableBackground" class="clickable-background">
      <article
        ref="wrapper"
        :class="[
          $style.wrapper,
          extraWide ? $style.widemodal : '',
          fullPage ? $style.fullPageWrapper : '',
        ]"
        :style="maxWidth ? `max-width: ${maxWidth}` : ''"
        tabindex="0"
        role="document"
      >
        <header>
          <h3 :id="idPrefix + 'modal'" :class="$style.title" :data-qa="idPrefix + 'modal-title'">
            {{ title }}
          </h3>
          <button
            v-if="!noCloseButton && !fullPage"
            :data-label="title"
            :data-eventcategory="trackCategory"
            :class="$style.close"
            type="button"
            data-action="close"
            :data-qa="idPrefix + 'modal-button-close'"
            class="js-track-click"
            @click="close"
          >
            <icon title="Close" icon="close" />
          </button>
          <button
            v-if="!noCloseButton && fullPage"
            :data-label="title"
            :data-eventcategory="trackCategory"
            :class="$style.closePill"
            type="button"
            data-action="close"
            :data-qa="idPrefix + 'modal-button-close'"
            class="js-track-click"
            @click="close"
          >
            {{ i18n.t('/modal/close') }} &times;
          </button>
        </header>
        <div :class="$style.content" :data-qa="idPrefix + 'modal-content'">
          <slot />
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { ref, getCurrentInstance } from 'vue'

export default {
  name: 'Modal',
  components: {
    Icon,
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus()
      },
    },
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    trackCategory: {
      type: String,
      default: '',
    },
    extraWide: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: '',
    },
    usingShowHide: {
      type: Boolean,
      default: false,
    },
    noCloseButton: {
      type: Boolean,
      default: false,
    },
    isCustomHtml: {
      type: Boolean,
      default: false,
    },
    fullPage: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: Number,
      default: null,
    },
  },
  emits: ['close'],
  setup() {
    const wrapper = ref(null)
    const clickableBackground = ref(null)

    return {
      wrapper,
      clickableBackground,
    }
  },
  data() {
    return {
      nodes: null,
      lastFocus: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    modalStyle() {
      return this.zIndex ? { zIndex: this.zIndex } : {}
    },
    idPrefix() {
      return this.id ? `${this.id}-` : ''
    },
  },
  mounted() {
    if (!this.usingShowHide) {
      document.body.style.overflow = 'hidden'
    }

    this.lastFocus = document.activeElement

    this.nodes = Array.from(document.querySelectorAll('*'))
    this.nodes.forEach((node) => {
      node.addEventListener('focus', this.restrictFocusOfNodesToModal)
    })

    document.addEventListener('keydown', this.closeByEscapeKey)
  },
  beforeUnmount() {
    if (!this.usingShowHide) {
      document.body.style.removeProperty('overflow')
    }
    this.nodes.forEach((node) => {
      node.removeEventListener('focus', this.restrictFocusOfNodesToModal)
    })
    document.removeEventListener('keydown', this.closeByEscapeKey)

    // restore the focus to the last focusedbutton
    this.lastFocus.focus()
  },
  methods: {
    // keep the focus inside the modal
    restrictFocusOfNodesToModal(event) {
      if (this.wrapper && !this.wrapper.contains(event.target)) {
        event.stopPropagation()
        this.wrapper.focus()
      }
    },
    closeByEscapeKey(event) {
      if (event.keyCode === 27) {
        this.$emit('close', event)
      }
    },
    closeByBackgroundClick(event) {
      // only close when mouse down is outside the modal
      // Use child element
      if (event.target !== this.clickableBackground) return
      this.close(event)
    },
    close(event) {
      this.$emit('close', event)
    },
  },
}
</script>

<style lang="scss" scoped>
.clickable-background {
  @include breakpoint(md) {
    margin: -$gutter;
  }
}
</style>

<style scoped module lang="scss">
.modal {
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: z-index(modal);
  background-color: rgba(0, 0, 0, 0.5);

  @include breakpoint(md) {
    padding: $gutter;
  }
}

.customHtmlStyles {
  list-style-position: inside;
}

.wrapper {
  max-width: $screen-md;
  position: relative;
  transition: transform 0.2s ease-out;
  min-height: 100vh;
  background: $white;

  @include breakpoint(md) {
    margin: 3em auto 1em;
    min-height: 0;
  }
}

.widemodal {
  max-width: $screen-lg;
}

header {
  background: $white;
  display: flex;
  align-items: self-start;
  padding: $gutter $gutter / 2;

  @include breakpoint(md) {
    padding: $gutter;
  }
}

.title {
  flex: 1 1 auto;
  padding-right: 0.25em;
  margin-bottom: 0;
}

.content {
  padding: 0 $gutter / 2 $gutter $gutter / 2;

  @include breakpoint(md) {
    padding: 0 $gutter $gutter;
  }
}

.fullPageWrapper {
  max-width: 100% !important;
  margin: 0 !important;
  min-height: 100vh;
  min-height: 100dvh;
}

.close {
  display: flex;
  justify-content: center;
  align-items: center;
  background: $gray-lightest;
  border-radius: 50%;
  width: 2em;
  height: 2em;
  flex: 0 0 auto;

  &:hover {
    background: $gray-light;
  }

  svg {
    @include icon;
  }
}

.closePill {
  background: transparent;
  border: 1px solid $gray-light;
  border-radius: 2rem;
  padding: 0.35rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  color: $gray-darker;
  flex: 0 0 auto;

  &:hover {
    background: $gray-lightest;
  }
}
</style>
